/* Zana's actions in the console (2026-10-28): the classic dashboard's
   handleZanaIntent, case for case, on the console's model. Every write and
   service call is the classic one, and so is every sentence she says back;
   only where she lands changes — a console page instead of a classic tab.

   Two differences, both said in her reply rather than hidden:
   - start_takeover (a narrated walkthrough) drove the classic screens; here
     it opens the page the job is on.
   - invite_driver never carried an address (the server sends "open Accounts"
     instead), so it opens the driver invite dialog. */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  addDoc, collection, deleteDoc, doc, getDoc, getDocs, query, serverTimestamp, setDoc, updateDoc, where, writeBatch,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import type { ZanaIntent } from '../../services/zanaActions';
import {
  cancelReportSchedule, describeCadence, generateReport, listReportSchedules, requestFuelReadings, saveReportFile, scheduleReport,
} from '../../services/zanaReports';
import { executeZanaAdminIntent } from '../../services/zanaAdminActions';
import {
  checkRoadsWithGoogle, invalidateZanaContext, notifyBusDriver, runZanaConnectionTest, sendAlertDigest,
} from '../../services/briefingsApi';
import { kindOf, severityOf, severityRank } from '../../fleet/alertMeta';
import { logBusStatusEvent } from '../../fleet/busStatusEvents';
import { currentMonthKey } from '../../components/FuelPricesPage';
import { PAGES } from '../kit';
import type { Bus, FleetAlert, FleetModel } from './model';
import { useFleet } from './FleetData';
import { addBus, plateKey, removeBus, rosterOf } from './busActions';
import { isSuspended, suspendDriver } from './peopleActions';

const DAY = 86400000;

/** A classic tab, as a console page. */
export const TAB_PAGE: Record<string, string> = {
  dashboard: PAGES.ConsoleOverview, fleet: PAGES.ConsoleBuses, accounts: PAGES.ConsolePeople,
  monthlyReport: PAGES.ConsoleReport, trips: PAGES.ConsoleTrips, liveMap: PAGES.ConsoleLiveMap,
  analytics: PAGES.ConsoleAnalytics, fuel: PAGES.ConsoleFuel, fuelPrices: PAGES.ConsoleFuelPrices,
};

const typeOf = (a: FleetAlert) => String(a.raw.type || a.kind || '');
const messageOf = (a: FleetAlert) => String(a.raw.message || a.body || a.title || '');
const busLabel = (f: FleetModel, busId: string | null | undefined, fallback = '') =>
  f.buses.find((b) => b.id === busId)?.plate || fallback || busId || 'a bus';

/** "YYYY-MM" → the first and last day, for the ranged report. */
function monthRange(month: string): { from: string; to: string } | null {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return null;
  const last = new Date(Date.UTC(Number(m[1]), Number(m[2]), 0)).getUTCDate();
  return { from: `${m[1]}-${m[2]}-01`, to: `${m[1]}-${m[2]}-${String(last).padStart(2, '0')}` };
}


export function useZanaExec(): (intent: ZanaIntent) => Promise<string | void> {
  const f = useFleet();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  return useCallback(async (intent: ZanaIntent): Promise<string | void> => {
    const code = f.org.code;
    const p = intent.params || {};
    const findBus = (raw?: string): Bus | null => {
      const key = plateKey(String(raw || ''));
      if (!key) return null;
      return f.buses.find((b) => plateKey(b.plate) === key || plateKey(b.id) === key || b.id === raw) || null;
    };
    const goBus = (b: Bus) => navigate(`${PAGES.ConsoleBuses}?bus=${encodeURIComponent(b.id)}`);
    const touched = () => { if (code) void invalidateZanaContext(code); };

    /* Buses that ran this week with no refill logged in it, and not parked
       ("acknowledged") since their last refill — classic's missingRefuelRows. */
    const missingRefuel = async (): Promise<{ bus: Bus }[]> => {
      const acks = new Map<string, number>();
      try {
        const snap = await getDocs(query(collection(db, 'fleetConditionAcks'), where('orgCode', '==', code), where('condition', '==', 'missing_refuel')));
        snap.docs.forEach((d) => {
          const a = d.data() as { busId?: string; ackedAt?: { toDate?: () => Date } };
          const ms = a.ackedAt?.toDate?.().getTime();
          if (a.busId && ms) acks.set(a.busId, ms);
        });
      } catch { /* no acknowledgements readable: treat none as parked */ }
      const now = Date.now();
      return f.buses.filter((b) => {
        if (b.status === 'off_road') return false;
        const lastFill = Math.max(0, ...f.refills.filter((r) => r.busId === b.id && r.at).map((r) => r.at!.getTime()));
        const ack = acks.get(b.id);
        if (ack && ack >= lastFill) return false;
        const lastRun = Math.max(0, ...f.runs.filter((r) => r.busId === b.id && r.finished).map((r) => r.finished!.getTime()));
        if (!lastRun || now - lastRun > 7 * DAY) return false;
        return !(lastFill && now - lastFill < 7 * DAY);
      }).map((bus) => ({ bus }));
    };

    switch (intent.actionId) {
      case 'export_report': {
        if (!code) return 'I need your organization before I can build a report.';
        const file = await generateReport(code, { preset: p.preset, from: p.from, to: p.to, format: p.format || 'pdf' });
        saveReportFile(file);
        return `Downloaded ${(p.format || 'pdf').toUpperCase()}: ${file.periodLabel} — ${file.vehicles} vehicle(s), ${file.totals.runs} runs.`
          + (file.notes.length ? ` Note: ${file.notes[0]}` : '');
      }
      case 'schedule_report': {
        if (!code) return 'I need your organization before I can schedule anything.';
        const res = await scheduleReport(code, {
          cadence: String(p.cadence || 'weekly'), hourUtc: p.hourUtc, weekday: p.weekday, dayOfMonth: p.dayOfMonth, format: p.format || 'pdf',
        });
        if (res.duplicate) return res.message || 'That schedule already exists.';
        return `Done — ${describeCadence({
          cadence: (p.cadence as 'daily' | 'weekly' | 'monthly') || 'weekly', hourUtc: Number(p.hourUtc ?? 6),
          weekday: p.weekday ?? null, dayOfMonth: p.dayOfMonth ?? null, format: (p.format as 'pdf' | 'csv') || 'pdf',
        })}.${res.email ? ` It goes to ${res.email}.` : ''} Ask me to stop it any time.`;
      }
      case 'request_fuel_readings': {
        if (!code) return 'I need your organization before I can message your drivers.';
        return (await requestFuelReadings(code, Array.isArray(p.onlyBuses) ? p.onlyBuses : undefined)).message;
      }
      case 'list_report_schedules': {
        if (!code) return 'I need your organization first.';
        const rows = await listReportSchedules(code);
        if (!rows.length) return 'You have no scheduled reports. Ask me to email you one daily, weekly or monthly.';
        return `You have ${rows.length} scheduled report${rows.length === 1 ? '' : 's'}:\n${rows.map((r) => `• ${describeCadence(r)} → ${r.email}`).join('\n')}`;
      }
      case 'cancel_report_schedule': {
        if (!code) return 'I need your organization first.';
        if (!p.id) return 'I need to know which scheduled report to stop — ask me to list them first.';
        const res = await cancelReportSchedule(code, String(p.id));
        return `Stopped: ${res.describe || 'that scheduled report'}. Nothing else changed.`;
      }
      case 'invite_driver':
        navigate(`${PAGES.ConsolePeople}?invite=drivers`);
        return 'Type the driver’s email and I’ll send the invite.';
      case 'list_unsigned_accounts':
      case 'contact_late_bus_parents':
      case 'prepare_school_trip_draft':
      case 'export_monthly_report': {
        if (!code) return 'I need your organization before I can run that admin action.';
        const result = await executeZanaAdminIntent(intent, {
          orgCode: code,
          // Every admin write reaches here only after the confirm chip.
          confirmed: true,
          exportMonthlyReport: async (month) => {
            const range = monthRange(month);
            if (!range) throw new Error('That month isn’t one I can build.');
            saveReportFile(await generateReport(code, { ...range, format: 'csv' }));
          },
          resolveInviteEmail: () => null,
          sendDriverInvite: async () => ({ ok: false, error: 'Invite drivers from Drivers and parents.' }),
        });
        return result.message;
      }
      case 'test_zana_connection':
        if (!code) return 'I need your organization to run a connection test.';
        return runZanaConnectionTest(code);
      case 'list_alerts_by_priority': {
        const unread = f.alerts.filter((a) => !a.read);
        if (!unread.length) return 'No unread alerts right now — the queue is clear.';
        const groups = new Map<string, { top: FleetAlert; count: number }>();
        for (const a of unread) {
          const key = `${kindOf(typeOf(a))}::${a.busId}`;
          const g = groups.get(key);
          if (!g) groups.set(key, { top: a, count: 1 });
          else { g.count++; if ((a.at?.getTime() || 0) > (g.top.at?.getTime() || 0)) g.top = a; }
        }
        const sorted = [...groups.values()].sort((x, y) => severityRank(severityOf(typeOf(x.top))) - severityRank(severityOf(typeOf(y.top)))
          || (y.top.at?.getTime() || 0) - (x.top.at?.getTime() || 0));
        const lines = sorted.slice(0, 6).map((g, i) => `${i + 1}. ${g.top.busPlate || busLabel(f, g.top.busId)} — ${messageOf(g.top).slice(0, 140)}${g.count > 1 ? ` ×${g.count}` : ''}`);
        const more = sorted.length > 6 ? ` (+${sorted.length - 6} more kinds in Alerts)` : '';
        return `${unread.length} unread alert(s) in ${sorted.length} group(s), worst first: ${lines.join(' · ')}${more}`;
      }
      case 'list_buses_missing_refuel': {
        const missing = await missingRefuel();
        if (!missing.length) return 'Every active bus that has been running has a refill logged in the last 7 days.';
        const lines = missing.slice(0, 8).map((r, i) => `${i + 1}. ${r.bus.plate}`);
        const more = missing.length > 8 ? ` (+${missing.length - 8} more)` : '';
        return `${missing.length} ${missing.length === 1 ? 'bus has' : 'buses have'} no refill logged in 7 days: ${lines.join(' · ')}${more}. Flagging puts them on the alert list for someone to chase; if you already know about them, I can park them instead so they stop coming back.`;
      }
      case 'check_roads_with_google': {
        const plate = String(p.plate || '').trim();
        const bus = plate ? findBus(plate) : null;
        if (!bus) return plate ? `I could not find ${plate} in this fleet, so I did not check anything.` : 'Tell me which bus and I’ll check its roads.';
        if (!code) return 'I need to know which school this is before I can check roads.';
        const raw = String(p.side || '').toLowerCase();
        const side: 'morning' | 'evening' = raw === 'evening' ? 'evening' : raw === 'morning' ? 'morning' : (new Date().getHours() < 12 ? 'morning' : 'evening');
        return checkRoadsWithGoogle(code, bus.id, side);
      }
      case 'list_parent_dropoff_flags': {
        const flags = f.alerts.filter((a) => typeOf(a) === 'dropoff_disputed_by_parent')
          .sort((x, y) => Number(x.read) - Number(y.read) || (y.at?.getTime() || 0) - (x.at?.getTime() || 0));
        if (!flags.length) return 'No parent has disputed a drop-off. That is the good outcome — parents are told where their child got off, and none of them has come back to say it was wrong.';
        const open = flags.filter((a) => !a.read);
        const lines = flags.slice(0, 6).map((a, i) => {
          const r = a.raw as { childName?: string; note?: string; dropLat?: number; dropLng?: number };
          const where_ = typeof r.dropLat === 'number' && typeof r.dropLng === 'number' ? ` · dropped at ${r.dropLat.toFixed(4)}, ${r.dropLng.toFixed(4)}` : '';
          return `${i + 1}. ${r.childName || 'A child'} on ${a.busPlate || busLabel(f, a.busId)}${where_}${r.note ? ` · parent said: "${r.note}"` : ''}${a.read ? ' (already reviewed)' : ''}`;
        });
        navigate(PAGES.ConsoleAlerts);
        const head = open.length ? `${open.length} ${open.length === 1 ? 'drop-off is' : 'drop-offs are'} still waiting on someone` : 'Nothing outstanding — every one has been looked at';
        return `${head}. ${lines.join(' · ')}${flags.length > 6 ? ` (+${flags.length - 6} more)` : ''}. They’re on Alerts; ring the parent or the driver to settle each one. I won’t close these myself — where a child ended up is not mine to decide.`;
      }
      case 'flag_buses_missing_refuel': {
        if (!code) return 'I can’t flag buses without an organization context.';
        const missing = await missingRefuel();
        if (!missing.length) {
          return intent.confirmRequired
            ? 'I can’t flag anything — by the time you confirmed, every active bus had a refuel logged in the last 7 days. The list moved while we were talking.'
            : 'Nothing to flag — every active bus already has a refuel log in 7 days.';
        }
        // Read or unread: a week is the horizon before the nag earns a repeat.
        const since = Date.now() - 7 * DAY;
        const already = new Set(f.alerts.filter((a) => /missing_refuel|no_refuel/i.test(typeOf(a)) && (a.at?.getTime() || 0) >= since).map((a) => a.busId));
        const toAdd = missing.filter((r) => !already.has(r.bus.id));
        if (!toAdd.length) return 'Already flagged in the last 7 days — raising it again would just duplicate the list. Say “park them” and I’ll acknowledge them instead so they stop resurfacing.';
        await Promise.all(toAdd.map((r) => addDoc(collection(db, 'fleetAlerts'), {
          schoolCode: code, busId: r.bus.id, busName: r.bus.plate, type: 'missing_refuel',
          message: `${r.bus.plate} has no refuel logged in 7 days — have the driver log litres so we can see what the tank is doing.`,
          timestamp: serverTimestamp(), read: false, source: 'zana', severity: 'warning',
        })));
        navigate(PAGES.ConsoleAlerts);
        touched();
        return `Flagged ${toAdd.length === 1 ? '1 bus' : `${toAdd.length} buses`} on Alerts so someone chases the log. That raises it — it doesn’t settle it: the bus still has no refuel on file until a driver logs litres and odometer. If you already know why, tell me to park it and it stops coming back.`;
      }
      case 'triage_high_risk': {
        const unread = f.alerts.filter((a) => !a.read);
        if (!unread.length) return 'The alert list is already clear — nothing to triage.';
        // Only `info` is cleared — what the reply has always promised.
        const keep = unread.filter((a) => severityOf(typeOf(a)) !== 'info');
        const routine = unread.filter((a) => severityOf(typeOf(a)) === 'info');
        for (let i = 0; i < routine.length; i += 450) {
          const batch = writeBatch(db);
          routine.slice(i, i + 450).forEach((a) => batch.update(doc(db, 'fleetAlerts', a.id), { read: true }));
          await batch.commit();
        }
        let emailed = false;
        if (code) {
          emailed = (await sendAlertDigest(code, keep.slice(0, 25).map((a) => ({ busId: a.busId || undefined, busName: a.busPlate, type: typeOf(a), message: messageOf(a) })), routine.length)).emailed;
        }
        navigate(PAGES.ConsoleAlerts);
        touched();
        const cleared = routine.length
          ? `Cleared ${routine.length} routine ${routine.length === 1 ? 'notice' : 'notices'} — traffic and delays.`
          : 'Nothing in there was routine — everything on the list is a warning or worse, so I cleared nothing.';
        const kept = keep.length
          ? ` ${keep.length === 1 ? '1 alert needs' : `${keep.length} alerts need`} you, and ${keep.length === 1 ? 'it is' : 'they are'} still on the list: ${keep.slice(0, 4).map((a) => a.busPlate || busLabel(f, a.busId)).join(', ')}${keep.length > 4 ? ` +${keep.length - 4} more` : ''}.`
          : ' Nothing left in the unread alert list.';
        return `${cleared}${kept}${emailed ? ' I’ve emailed you the shortlist.' : ' I couldn’t get the email out, so the shortlist is here on screen only.'}`;
      }
      case 'ack_missing_refuel': {
        if (!code) return 'I can’t park anything without an organization context.';
        const missing = await missingRefuel();
        if (!missing.length) return 'Nothing to park — no bus is showing a missing refuel log right now.';
        const scope = findBus(p.busId);
        const targets = scope ? missing.filter((r) => r.bus.id === scope.id) : missing;
        if (!targets.length) return `${scope?.plate} isn’t on the missing-refuel list, so there’s nothing to park.`;
        await Promise.all(targets.map((r) => setDoc(doc(db, 'fleetConditionAcks', `${code}__missing_refuel__${r.bus.id}`), {
          orgCode: code, condition: 'missing_refuel', busId: r.bus.id, busName: r.bus.plate,
          ackedAt: serverTimestamp(), ackedBy: currentUser?.uid || '', source: 'zana',
        }, { merge: true })));
        return `Parked ${targets.length === 1 ? targets[0].bus.plate : `${targets.length} buses`}. They’re off the fuel report and out of my count until one of them logs a refuel, which lets a fresh dry spell surface again.`;
      }
      case 'unack_missing_refuel': {
        if (!code) return 'I can’t un-park anything without an organization context.';
        const scope = findBus(p.busId);
        const snap = await getDocs(query(collection(db, 'fleetConditionAcks'), where('orgCode', '==', code), where('condition', '==', 'missing_refuel')));
        const acked = snap.docs.filter((d) => !scope || d.data().busId === scope.id);
        if (!acked.length) return 'Nothing is parked right now.';
        await Promise.all(acked.map((d) => deleteDoc(d.ref)));
        return `Back on the list — ${acked.length === 1 ? '1 bus' : `${acked.length} buses`} will show up in the fuel report again.`;
      }
      case 'draft_driver_coach_note': {
        const driverId = String(p.driverId || '').trim();
        const note = String(p.note || '').trim().slice(0, 400);
        const label = String(p.driverLabel || driverId).trim();
        if (!driverId || !note) return 'I need a driver and a note to draft — ask me again with a driver in mind.';
        if (!code) return 'I can’t save a coach note without an organization context.';
        await setDoc(doc(db, 'driverInsights', driverId), {
          orgCode: code, driverId, draftNote: note, draftNoteLabel: label, draftNoteSource: 'zana', draftNoteAt: serverTimestamp(), updatedAt: serverTimestamp(),
        }, { merge: true });
        return `Saved a coaching draft for ${label}: "${note.slice(0, 120)}${note.length > 120 ? '…' : ''}".`;
      }
      case 'email_driver': {
        if (!code) return 'I need your organization to reach a driver.';
        const scope = findBus(p.busId);
        if (!scope) return 'Tell me which bus and I’ll write to whoever drives it.';
        const note = String(p.note || '').trim();
        if (!note) return `I need something to say to ${scope.plate}’s driver.`;
        const out = await notifyBusDriver(code, scope.id, note);
        if (out.ok) return `Sent — ${out.driverName || 'the driver'} on ${out.plate || scope.plate} has it, and replies come back to you.`;
        if (out.reason === 'no_driver') return `${scope.plate} has no driver assigned yet, so there’s nobody to write to.`;
        if (out.reason === 'no_email') return `${scope.plate}’s driver has no email on file — that’s the only channel we have today. Add one in Drivers and parents and I’ll send it.`;
        if (out.reason === 'email_not_configured') return 'Email isn’t configured on the server, so I couldn’t send that.';
        if (out.reason === 'http_403' && out.message) return out.message;
        return `I couldn’t get that email out (${out.reason || 'unknown error'}).`;
      }
      case 'resolve_incidents': {
        if (!code) return 'I need your organization first.';
        const scope = findBus(p.busId);
        // orgCode in the query is what lets the rules prove the read is ours.
        const snap = await getDocs(query(collection(db, 'busIssueReports'), where('orgCode', '==', code)));
        const open = snap.docs.filter((d) => d.data().resolved !== true && (!scope || d.data().busId === scope.id));
        if (!open.length) return scope ? `${scope.plate} has no open incident reports.` : 'No open incident reports — nothing to close out.';
        for (let i = 0; i < open.length; i += 450) {
          const batch = writeBatch(db);
          open.slice(i, i + 450).forEach((d) => batch.update(d.ref, { resolved: true, resolvedAt: serverTimestamp(), resolvedBy: currentUser?.uid || null, resolvedNote: 'Closed from Zana' }));
          await batch.commit();
        }
        touched();
        const buses = new Set(open.map((d) => d.data().busId)).size;
        return `Closed out ${open.length} incident report(s) across ${buses} bus(es)${scope ? ` — ${scope.plate}` : ''}. The reports stay on each bus’s history.`;
      }
      case 'mark_alerts_reviewed': {
        const scope = findBus(p.busId);
        const targets = f.alerts.filter((a) => !a.read && (!scope || a.busId === scope.id));
        if (!targets.length) return scope ? `${scope.plate} has no unread alerts — nothing to mark.` : 'No unread alerts to mark — the list is already clear.';
        for (let i = 0; i < targets.length; i += 450) {
          const batch = writeBatch(db);
          targets.slice(i, i + 450).forEach((a) => batch.update(doc(db, 'fleetAlerts', a.id), { read: true }));
          await batch.commit();
        }
        touched();
        return `Done — marked ${targets.length} alert(s) reviewed${scope ? ` for ${scope.plate}` : ''}.`;
      }
      case 'open_high_risk_alerts':
        navigate(PAGES.ConsoleAlerts);
        return;
      case 'open_fuel_report':
        navigate(PAGES.ConsoleFuel);
        return;
      case 'open_zana_mode':
        // Zana's briefing (classic "Zana Mode") is on her full page: Regenerate and the savings card.
        navigate(PAGES.ConsoleZanaChat);
        return;
      case 'open_tab': {
        const to = TAB_PAGE[String(p.tab || '').trim()];
        if (!to) return `I can’t open “${p.tab || 'that'}” — it isn’t a page on this dashboard.`;
        navigate(to);
        return;
      }
      case 'spotlight':
      case 'open_bus': {
        const b = findBus(p.busId || p.entityId);
        if (b) goBus(b);
        return;
      }
      case 'delete_bus': {
        const target = findBus(p.busId);
        if (!target) return 'I need a plate I know in this fleet before I can delete a bus.';
        const kids = rosterOf(target).length;
        if (kids > 0) {
          goBus(target);
          return `${target.plate} still has ${kids === 1 ? '1 child' : `${kids} children`} on its list — move them first, then remove the bus from its page.`;
        }
        await removeBus(target);
        touched();
        return `Removed ${target.plate} from the active fleet. Its trips and fuel stay in your reports.`;
      }
      case 'suspend_bus': {
        const target = findBus(p.busId);
        if (!target) return 'I need a plate I know in this fleet before I can suspend a bus.';
        const kids = rosterOf(target).length;
        if (kids > 0) {
          goBus(target);
          return `${target.plate} still has ${kids === 1 ? '1 child' : `${kids} children`} on its list — move them first, then I can take it off the road.`;
        }
        await updateDoc(doc(db, 'buses', target.id), { status: 'suspended', updatedAt: serverTimestamp() });
        if (code) {
          void logBusStatusEvent({
            busId: target.id, orgCode: code, plate: target.plate, busName: target.name, fromStatus: String(target.raw.status || 'active'),
            toStatus: 'suspended', byUid: currentUser?.uid ?? null, byEmail: currentUser?.email ?? null, source: 'zana', childCount: 0,
          });
        }
        touched();
        return `${target.plate} is now off the road (suspended). Put it back from its bus page when it runs again.`;
      }
      case 'create_bus': {
        if (!code || !currentUser) return 'I need your organization before I can create a bus.';
        const r = await addBus(f, currentUser.uid, { plate: String(p.plate || ''), seats: '', makeModel: '', driverId: null, nickname: String(p.nickname || '') });
        if (!r.ok) return r.message;
        touched();
        navigate(`${PAGES.ConsoleBuses}?bus=${encodeURIComponent(r.plate)}`);
        return r.revived ? `${r.plate} is back in your fleet.` : `Created ${r.plate} as an active bus. Choose its driver on the bus page when you’re ready.`;
      }
      case 'apply_station_pump_price': {
        if (!code) return 'I need your organization before I can update a station price.';
        const seen = f.alerts.find((a) => typeOf(a) === 'station_pump_price' && !a.read) || f.alerts.find((a) => typeOf(a) === 'station_pump_price');
        const raw = (seen?.raw || {}) as { placeId?: string; observedPerL?: number; fuelType?: string; stationName?: string };
        const placeId = String(p.placeId || raw.placeId || '').trim();
        const observed = Number(p.pricePerL ?? raw.observedPerL);
        const fuelType = String(p.fuelType || raw.fuelType || 'diesel').toLowerCase() === 'petrol' ? 'petrol' : 'diesel';
        const stationName = String(p.stationName || raw.stationName || '').trim();
        if (!placeId || !Number.isFinite(observed) || observed < 50 || observed > 500) {
          navigate(PAGES.ConsoleFuelPrices);
          return 'I don’t have a pump reading to put on the list yet. When a driver corrects litres at a station, I’ll get one — or add the station under Fuel prices.';
        }
        const month = currentMonthKey();
        const ref = doc(db, 'fuelPrices', `${code}__${month}`);
        const data = ((await getDoc(ref)).data() || {}) as Record<string, unknown>;
        const stations = Array.isArray(data.stations) ? (data.stations as Record<string, unknown>[]).map((s) => ({ ...s })) : [];
        const idx = stations.findIndex((s) => String(s.placeId || '') === placeId && String(s.fuelType || 'diesel').toLowerCase() === fuelType);
        const row = {
          placeId, name: stationName || String(stations[idx]?.name || placeId), pricePerL: Number(observed.toFixed(2)), fuelType,
          ...(idx >= 0 && stations[idx].primary === true ? { primary: true } : {}),
        };
        if (idx >= 0) stations[idx] = { ...stations[idx], ...row }; else stations.push(row);
        await setDoc(ref, { orgCode: code, month, source: 'zana', stations, updatedBy: currentUser?.uid ?? null, updatedAt: serverTimestamp() }, { merge: true });
        const matching = f.alerts.filter((a) => typeOf(a) === 'station_pump_price' && !a.read && ((a.raw as { placeId?: string }).placeId === placeId || a.id.includes(placeId)));
        await Promise.all(matching.map((a) => updateDoc(doc(db, 'fleetAlerts', a.id), { read: true })));
        touched();
        navigate(PAGES.ConsoleFuelPrices);
        return `Put ${stationName || placeId} on this month’s list at Ksh ${observed.toFixed(2)}/L. The next fill there uses that price.`;
      }
      case 'suspend_driver': {
        const q = String(p.accountQuery || '').trim().toLowerCase();
        let person = p.driverId ? f.drivers.find((d) => d.id === p.driverId) || null : null;
        if (!person && q) {
          const hits = f.drivers.filter((d) => d.name.toLowerCase().includes(q) || d.email.toLowerCase().includes(q) || (d.name && q.includes(d.name.toLowerCase())));
          if (hits.length === 1) person = hits[0];
          else if (hits.length > 1) {
            navigate(PAGES.ConsolePeople);
            return `I found ${hits.length} drivers matching “${p.accountQuery}” — pick the right one in Drivers and parents.`;
          }
        }
        if (!person) {
          navigate(PAGES.ConsolePeople);
          return 'I couldn’t match that driver to one person — suspend them from Drivers and parents.';
        }
        if (isSuspended(person)) return `${person.name || 'That driver'} is already suspended.`;
        await suspendDriver(person, true);
        return `Suspended ${person.name || 'the driver'} — they’re signed out until you unsuspend.`;
      }
      case 'start_takeover': {
        // The walkthroughs drive the classic screens; here she opens the page.
        const topic = `${p.scriptId || ''} ${p.topic || ''} ${p.title || ''}`.toLowerCase();
        const to = /fuel|refill|litre/.test(topic) ? PAGES.ConsoleFuel
          : /alert|risk|incident/.test(topic) ? PAGES.ConsoleAlerts
            : /driver|parent|account|invite/.test(topic) ? PAGES.ConsolePeople
              : /trip/.test(topic) ? PAGES.ConsoleTrips
                : /service|maint|insur|inspect/.test(topic) ? PAGES.ConsoleMaintenance
                  : /report/.test(topic) ? PAGES.ConsoleReport
                    : /bus|fleet|plate/.test(topic) ? PAGES.ConsoleBuses : null;
        if (to) navigate(to);
        return to ? 'Here’s where that is — I can’t walk you through it on this page yet.' : 'I can’t walk you through that on this page yet.';
      }
      default:
        return undefined;
    }
  }, [f, navigate, currentUser]);
}
